import {
  attr,
  Fragment,
  html,
  Signal,
  TNode,
  Use,
  Value,
  When,
} from '@tempots/dom'
import { Anchor, Location } from '@tempots/ui'
import { Logo } from './logo'

export interface TopBarOptions {
  /** Where the logo links to ('/' for public pages, workspace for the app). */
  logoHref: Value<string>
  /** Mobile-only hamburger menu, rendered before the logo below the md breakpoint. */
  menu?: TNode
  /** Desktop navigation links, rendered after the logo (hidden below md). */
  nav?: TNode
  /** Right-aligned content: contextual actions, agency selector, user menu. */
  end?: TNode
}

/**
 * Single top bar shared by the authenticated app layout and the public trial
 * page: logo + navigation on the left, contextual actions and identity on the
 * right.
 */
export function TopBar({ logoHref, menu, nav, end }: TopBarOptions) {
  return html.div(
    attr.class(
      'flex flex-row justify-between items-center gap-2 px-1 sm:px-2 py-1 sm:py-2 bg-white border-b border-gray-200 flex-shrink-0'
    ),
    html.div(
      attr.class('flex items-center gap-1 sm:gap-2 min-w-0'),
      menu ? html.div(attr.class('md:hidden'), menu) : null,
      Anchor({ href: logoHref }, Logo({ size: 'header' })),
      nav
        ? html.nav(
            attr.class('hidden md:flex items-center gap-1 ml-2 lg:ml-4'),
            nav
          )
        : null
    ),
    html.div(attr.class('flex items-center gap-2 sm:gap-3'), end ?? null)
  )
}

const NAV_SECTIONS = [
  { segment: 'workspace', label: 'Workspace' },
  { segment: 'customize', label: 'Customize', requiresTemplateAccess: true },
  { segment: 'agency', label: 'Agency' },
] as const

/**
 * Section navigation for the authenticated app. An explicit Workspace link
 * means the agency selector no longer doubles as the way "home".
 * Customize is only shown to roles that may edit the agency template.
 */
export function TopNavLinks({
  agencyId,
  canCustomize,
}: {
  agencyId: Signal<string>
  canCustomize: Signal<boolean>
}) {
  return Use(Location, location =>
    Fragment(
      ...NAV_SECTIONS.map(section => {
        const link = () =>
          Anchor(
            { href: agencyId.map(id => `/${id}/${section.segment}`) },
            attr.class(
              location.pathname.map((path): string =>
                path.endsWith(`/${section.segment}`)
                  ? 'px-3 py-1.5 rounded-md text-sm font-semibold text-teal-800 bg-teal-50'
                  : 'px-3 py-1.5 rounded-md text-sm font-medium text-gray-600 hover:text-teal-900 hover:bg-gray-100 transition-colors'
              )
            ),
            section.label
          )
        return 'requiresTemplateAccess' in section
          ? When(canCustomize, link)
          : link()
      })
    )
  )
}
